/**
 * 设备状态管理状态仓库
 * 使用 Pinia 进行状态管理
 *
 * 职责:
 * - 管理 HID 设备的连接状态
 * - 缓存设备详情和状态数据
 * - 提供命令发送接口
 * - 处理轮询和事件监听
 */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { hidManager } from '@/api/device/hid'
import { selectActiveDeviceStatus } from '@/api/device/protocol'
import type { DeviceDetail, DeviceStatus, ConnectionType, DeviceCapabilities, VoiceType } from '@/api/device/types'
import { DEFAULT_DEVICE_CAPABILITIES, DEVICE_CAPABILITY_MAP } from '@/api/device/types'
import { BRIDGE_ALL_CHANNELS, packCallBridgeByte, parseCallBridgeByte, sceneBridgeToHardwareByte } from '@/api/device/bridge'
import {
  buildSpeakerMuteCommand,
  buildMicMuteCommand,
  buildSpeakerVolumeCommand,
  buildMicVolumeCommand,
  buildEncCommand,
  buildRgbCommand,
  buildPowerCommand,
  buildAudioModeCommand,
  buildScenarioApplyCommand,
  buildAudioBalanceCommand,
  buildSidetoneSwitchCommand,
  buildSidetoneVolumeCommand,
  buildEqLoginStatusCommand,
  buildBtShareSwitchCommand,
  buildCallBridgeSwitchCommand,
  buildVoiceEffectCommand,
} from '@/api/device/commands'

export const useDeviceStore = defineStore('device', () => {
  // 状态
  // isConnected: 仅表示 HID 通道已打开，不等于耳机链路已就绪
  const isConnected = ref(false)
  // isDeviceConnected: 业务链路是否真正连通（用于顶部状态展示/功能可用性）
  const isDeviceConnected = ref(false)
  const isConnecting = ref(false)
  const deviceDetail = ref<DeviceDetail | null>(null)
  const deviceStatus = ref<DeviceStatus | null>(null)
  const connectionType = ref<ConnectionType>(null)
  const error = ref<string | null>(null)
  const connectionStage = ref<string>('initial') // initial/requesting/opening/waiting_pairing/waiting_usb/connected/error
  const connectionMessage = ref<string | null>(null)
  const isRefreshing = ref(false) // 防止 refreshStatus 并发执行

  // 断线保护机制
  let deviceStatusMissCount = 0
  const MAX_STATUS_MISSES = 3
  let disconnectGuardTimer: NodeJS.Timeout | null = null
  const DISCONNECT_GUARD_MS = 200
  let statusPollingTimer: ReturnType<typeof setInterval> | null = null
  let statusPollingStarting = false
  let statusPollingSuspended = false
  const STATUS_POLL_INTERVAL_MS = 2000

  // 吃鸡模块采用动态导入以避免循环依赖。
  // 0x80 在模块首次加载完成前可能连续到达，如果每次都直接 import().then()，
  // 会在模块加载完成的同一时刻把之前积压的多次同步全部执行，造成控制台连续打印多遍。
  // 这里只保留最新一份硬件状态，模块可用后同步一次。
  let pendingChijiHardwareStatus: { enabled: boolean; tag: number } | null = null
  let chijiSyncTask: Promise<void> | null = null

  function queueChijiHardwareStatusSync(value: { enabled: boolean; tag: number }): void {
    pendingChijiHardwareStatus = value
    if (chijiSyncTask) return

    chijiSyncTask = import('./chiji')
      .then(({ useChijiStore }) => {
        const latest = pendingChijiHardwareStatus
        pendingChijiHardwareStatus = null
        if (latest) useChijiStore().syncHardwareStatus(latest)
      })
      .finally(() => {
        chijiSyncTask = null
        // import 期间如果又来了新 0x80，只补同步最后一份。
        if (pendingChijiHardwareStatus) {
          queueChijiHardwareStatusSync(pendingChijiHardwareStatus)
        }
      })
  }

  function emitDeviceConnectionTip(kind: 'inserted' | 'removed', connectionType: ConnectionType): void {
    if (!connectionType || typeof window === 'undefined') return
    window.dispatchEvent(new CustomEvent('device-connection-tip', {
      detail: { kind, connectionType },
    }))
  }

  // 降噪设置（1-6档，对齐旧版 server-1.js）
  const noiseEnabled = ref(false)
  const noiseLevel = ref(1) // 默认1档，范围1-6
  const lastNoiseLevel = ref(1) // 记录关闭前的档位
  // 0x72 关 ENC 时固件可能仍回报上一次开启的降噪档位（例如 Gaming 的 6）。
  // 页面保持用户真正选择的“关闭时备用档位”，不让过期 0x80 覆盖。
  // 只在本次设备连接内有效；开启降噪后重新以硬件 0x80 为准。
  let noiseLevelWhileDisabled: number | null = null

  // 静音状态
  const speakerMuted = ref(false)
  const micMuted = ref(false)

  // 音量设置 (0-100)
  const speakerVolume = ref(50)
  const micVolume = ref(50)

  // RGB设置
  const rgbEnabled = ref(false)
  const rgbMode = ref(0)

  // RGB 写命令后，轮询可能先返回一次旧 ledMode。写入确认前保留用户刚选的本地值，
  // 防止灯效下拉选中块和底部按钮文案短暂闪回旧模式。
  let pendingRgbWrite: { enabled: boolean; mode: number; expiresAt: number } | null = null
  const RGB_WRITE_GUARD_MS = 5000

  function markPendingRgbWrite(enabled: boolean, mode: number): void {
    pendingRgbWrite = { enabled, mode, expiresAt: Date.now() + RGB_WRITE_GUARD_MS }
  }
  const rgbBrightness = ref(50)
  const lastRgbMode = ref(1)

  // 音频混合/分离
  const audioSplitEnabled = ref(false)
  const audioMode = ref<0 | 1 | 2>(0)
  const audioBalanceValue = ref(50) // 0-100, 50=中间
  const voiceEffect = ref<VoiceType>('normal')

  // 音频模式命令下发后，0x80 轮询可能会先返回一到数次旧模式。
  // 在硬件确认新模式前保留用户刚点击的本地状态，避免 Mix/Split/Adaptive
  // 选中块被旧回读覆盖后又跳回目标模式。
  let pendingAudioModeWrite: { mode: 0 | 1 | 2; expiresAt: number; requestId: number } | null = null
  let audioModeWriteRequestId = 0
  const AUDIO_MODE_WRITE_GUARD_MS = 5000

  function markPendingAudioModeWrite(mode: 0 | 1 | 2): number {
    const requestId = ++audioModeWriteRequestId
    pendingAudioModeWrite = {
      mode,
      expiresAt: Date.now() + AUDIO_MODE_WRITE_GUARD_MS,
      requestId,
    }
    return requestId
  }

  // Bridge 总开关和通路配置独立保存；0x0E = 总关闭，但保留三个已选通路。
  const callBridgeByte = ref(BRIDGE_ALL_CHANNELS)
  const callBridgeEnabled = computed(() => (callBridgeByte.value & 0x01) !== 0)
  const callBridgeChannelMask = computed(() => callBridgeByte.value & BRIDGE_ALL_CHANNELS)
  let lastCallBridgeChannelMask = BRIDGE_ALL_CHANNELS
  const callBridgeRestoreChannelMask = computed(() => callBridgeChannelMask.value || lastCallBridgeChannelMask)
  let confirmedCallBridgeByte = BRIDGE_ALL_CHANNELS
  let confirmedBridgeRestoreMask = BRIDGE_ALL_CHANNELS
  let callBridgeSession = 0
  let callBridgeRequestId = 0
  let callBridgeWrites = 0
  let callBridgeWriteQueue: Promise<void> = Promise.resolve()
  let pendingCallBridgeWrite: { byte: number; expiresAt: number } | null = null
  const BRIDGE_WRITE_GUARD_MS = 5000

  function applyCallBridgeByte(byte: number, status = deviceStatus.value): void {
    const fields = parseCallBridgeByte(byte)
    callBridgeByte.value = fields.callBridgeByte
    if (fields.callBridgeChannelMask) lastCallBridgeChannelMask = fields.callBridgeChannelMask
    if (status) Object.assign(status, fields)
  }

  function resetCallBridgeState(): void {
    callBridgeSession += 1
    callBridgeRequestId = 0
    callBridgeWrites = 0
    callBridgeWriteQueue = Promise.resolve()
    pendingCallBridgeWrite = null
    confirmedCallBridgeByte = BRIDGE_ALL_CHANNELS
    confirmedBridgeRestoreMask = BRIDGE_ALL_CHANNELS
    lastCallBridgeChannelMask = BRIDGE_ALL_CHANNELS
    callBridgeByte.value = BRIDGE_ALL_CHANNELS
  }

  function syncCallBridgeStatus(status: DeviceStatus): void {
    const reported = status.callBridgeByte ?? packCallBridgeByte(
      status.callBridgeEnabled === 1,
      status.callBridgeChannelMask ?? callBridgeChannelMask.value,
    )
    if (pendingCallBridgeWrite) {
      // 快速连点时仍只有一条完整状态命令/次；旧 0x80 不得把新选择闪回。
      if (callBridgeWrites > 0 || (reported !== pendingCallBridgeWrite.byte && Date.now() < pendingCallBridgeWrite.expiresAt)) {
        applyCallBridgeByte(pendingCallBridgeWrite.byte, status)
        return
      }
      pendingCallBridgeWrite = null
    }
    confirmedCallBridgeByte = reported
    if (reported & BRIDGE_ALL_CHANNELS) confirmedBridgeRestoreMask = reported & BRIDGE_ALL_CHANNELS
    applyCallBridgeByte(reported, status)
  }

  // 计算属性
  const deviceCategory = computed(() => {
    const fromDetail = deviceDetail.value?.deviceCategory
    if (fromDetail && fromDetail !== 'unknown') return fromDetail

    // 与旧项目保持一致：优先使用详情中的 deviceType，
    // 没有详情时使用状态中的 productType 分流（0=耳机，1=麦克风）。
    const pt = deviceStatus.value?.productType
    if (typeof pt === 'number') {
      if (pt === 1) return 'microPhone'
      return 'headPhone'
    }

    return fromDetail || 'unknown'
  })
  const isHeadPhone = computed(() => deviceCategory.value === 'headPhone')
  const isMicroPhone = computed(() => deviceCategory.value === 'microPhone')
  const batteryLevel = computed(() => deviceStatus.value?.batteryLevel ?? 0)
  // B[13] 只代表 USB 连线；是否充电只看 B[17]：1 充电中，2 已充满。
  const isCharging = computed(() => deviceStatus.value?.batteryCharging === 1)
  const isPowerOff = computed(() => (deviceStatus.value?.powerOffStatus || 0) === 1)

  // 设备能力配置（支持扩展新设备类型）
  const deviceCapabilities = computed<DeviceCapabilities>(() => {
    const category = deviceCategory.value
    const specificCaps = DEVICE_CAPABILITY_MAP[category] || {}
    return { ...DEFAULT_DEVICE_CAPABILITIES, ...specificCaps }
  })

  // 便捷的能力检查 getter
  const canUseEq = computed(() => deviceCapabilities.value.hasEq)
  const canUseNoiseReduction = computed(() => deviceCapabilities.value.hasNoiseReduction)
  const canUseSpecialAudio = computed(() => deviceCapabilities.value.hasSpecialAudio)
  const canUseLed = computed(() => deviceCapabilities.value.hasLed)
  const canUseHotkey = computed(() => deviceCapabilities.value.hasHotkey)
  const canUseLanguageSwitch = computed(() => deviceCapabilities.value.hasLanguageSwitch)
  const canUseFirmwareUpgrade = computed(() => deviceCapabilities.value.hasFirmwareUpgrade)
  const canUsePowerControl = computed(() => deviceCapabilities.value.hasPowerControl)
  const canUseVoiceChange = computed(() => deviceCapabilities.value.hasVoiceChange)

  // Audio Split 的蓝牙状态兼容规则：
  // - 旧协议：dongle <= V1.1.10 或耳机 <= V1.1.11，使用 bluetoothConnected
  // - 新协议：仅当 dongle > V1.1.10 且耳机 > V1.1.11，使用 bluetoothPlaying
  function compareFirmwareVersion(a: string | undefined, b: string): number {
    const parse = (value: string | undefined) => {
      const matched = String(value || '').match(/(\d+)\.(\d+)\.(\d+)/)
      return matched
        ? [Number(matched[1]), Number(matched[2]), Number(matched[3])]
        : null
    }

    const left = parse(a)
    const right = parse(b)
    if (!left || !right) return -1

    for (let i = 0; i < 3; i += 1) {
      if (left[i] > right[i]) return 1
      if (left[i] < right[i]) return -1
    }
    return 0
  }

  // 协议版本分界：
  // - 旧协议：dongle <= V1.1.10 或耳机 <= V1.1.11
  // - 新协议：dongle > V1.1.10 且耳机 > V1.1.11
  // 目前 Audio Split 的蓝牙播放判定、以及“耳机按键静音时禁用 RGB”都使用同一分界。
  const usesNewProtocol = computed(() => {
    const status = deviceStatus.value
    const detail = deviceDetail.value
    const dongleVersion = status?.dongleVersion || detail?.dongleVersion || ''
    const headsetVersion = status?.softwareVersion || detail?.softwareVersion || ''

    return compareFirmwareVersion(dongleVersion, '1.1.10') > 0
      && compareFirmwareVersion(headsetVersion, '1.1.11') > 0
  })

  const usesBluetoothPlayingForAudioSplit = computed(() => usesNewProtocol.value)

  const isBluetoothInCall = computed(() => Number(deviceStatus.value?.bluetoothInCall ?? 0) === 1)

  // 蓝牙通话中切换 Bridge：USB 只看耳机；2.4G 必须同时满足 dongle 和耳机版本。
  // 严格大于 V1.2.0；版本缺失或链路未知时不放开。与旧的 Audio Split/RGB 分界独立。
  const supportsCallBridgeDuringBluetoothCall = computed(() => {
    const headsetVersion = deviceStatus.value?.softwareVersion || deviceDetail.value?.softwareVersion || ''
    if (compareFirmwareVersion(headsetVersion, '1.2.0') <= 0) return false
    if (connectionType.value === 'USB') return true
    if (connectionType.value !== '2.4G') return false
    const dongleVersion = deviceStatus.value?.dongleVersion || deviceDetail.value?.dongleVersion || ''
    return compareFirmwareVersion(dongleVersion, '1.2.0') > 0
  })

  const callBridgeLockedByBluetoothCall = computed(() => (
    isBluetoothInCall.value && !supportsCallBridgeDuringBluetoothCall.value
  ))
  const btShareLockedByBluetoothCall = computed(() => isBluetoothInCall.value)

  // 只有“新协议 + 耳机物理按键静音”才锁定 RGB。
  // 网页静音时 hardwareMuted=0，因此 RGB 仍可正常切换；旧协议完全忽略 hardwareMuted。
  const rgbLockedByHardwareMute = computed(() => (
    usesNewProtocol.value
    && Number(deviceStatus.value?.hardwareMuted ?? 0) === 1
  ))

  const audioSplitBluetoothActive = computed(() => {
    const status = deviceStatus.value
    if (!status) return false

    if (usesBluetoothPlayingForAudioSplit.value) {
      return Number(status.bluetoothPlaying || 0) === 1
    }

    return Number(status.bluetoothConnected || 0) === 1
  })

  // 操作

  function syncAudioBalanceFromStatus(): void {
    const status = deviceStatus.value
    if (!status) return
    const ratio = audioMode.value === 1 ? status.splitVolume
      : audioMode.value === 2 ? status.adaptiveVolume : status.mixVolume
    audioBalanceValue.value = Math.max(0, Math.min(100, Math.round((ratio ?? 100) / 2)))
  }

  function syncDeviceStatus(status: DeviceStatus): void {
    deviceStatus.value = status
    syncCallBridgeStatus(status)
    connectionType.value = hidManager.getConnectionType()

    // 麦克风、喇叭、EQ、吃鸡的通用字段一并按当前链路选择，日志和页面含义一致。
    selectActiveDeviceStatus(status, connectionType.value)

    const isDongle = connectionType.value === '2.4G'
    const eqEnabled = isDongle ? status.dongleEqEnabled : status.usbEqEnabled
    const eqMode = isDongle ? status.dongleEqMode : status.usbEqMode
    const eqTag = isDongle ? status.dongleEqTag : status.usbEqTag
    void import('./eq').then(({ useEqStore }) => {
      useEqStore().syncHardwareStatus(
        Number(eqEnabled ?? status.eqEnabled ?? 0) === 1,
        Number(eqMode ?? status.eqMode ?? 0),
        Number(eqTag ?? status.eqTag ?? 0),
      )
    })
    const chijiEnabled = isDongle ? status.dongleChickenOneKey : status.usbChickenOneKey
    const chijiTag = isDongle ? status.dongleChickenTag : status.usbChickenTag
    queueChijiHardwareStatusSync({
      enabled: Number(chijiEnabled ?? status.chickenOneKey ?? 0) === 1,
      tag: Number(chijiTag ?? status.chickenTag ?? ((status.chickenByte ?? 0) & 0x7F)) || 0,
    })

    noiseEnabled.value = (status.noiseReductionEnabled || 0) === 1
    if (noiseEnabled.value) noiseLevelWhileDisabled = null
    const hardwareNoiseLevel = Math.max(1, Math.min(6, Number(status.noiseReductionMode) || 1))
    noiseLevel.value = !noiseEnabled.value && noiseLevelWhileDisabled !== null
      ? noiseLevelWhileDisabled : hardwareNoiseLevel
    // 旧 0x80 在 ENC 关闭时会报告上一档位；已明确恢复默认档位时不能覆盖。
    if (!noiseEnabled.value && noiseLevelWhileDisabled !== null) {
      status.noiseReductionMode = noiseLevelWhileDisabled
    }
    if (!noiseEnabled.value) lastNoiseLevel.value = noiseLevel.value

    speakerMuted.value = (status.speakerMute || 0) !== 0
    micMuted.value = (status.micMute || 0) !== 0
    speakerVolume.value = Math.max(0, Math.min(100, status.speakerVolumeLevel || 0))
    micVolume.value = Math.max(0, Math.min(100, status.micVolumeLevel || 0))

    const reportedRgbMode = status.ledMode || 0
    const reportedRgbEnabled = reportedRgbMode !== 0
    let acceptRgbHardwareStatus = true

    // 新协议下，耳机按键静音会由 hardwareMuted=1 明确标识。
    // 此时 RGB 要“保持静音前当前灯效”，不能让静音期间 0x80 的 ledMode
    // 把页面状态覆盖掉；同时后续 setRgbEnabled / setRgbMode 也会拒绝下发命令。
    if (rgbLockedByHardwareMute.value) {
      // 如果页面刚打开时设备已经处于物理静音，本地还没有任何 RGB 状态，
      // 但 0x80 仍能给出非 0 ledMode，则允许初始化一次；之后静音期间保持该灯效不再跟随。
      const canInitializeLockedRgb = !rgbEnabled.value && rgbMode.value === 0 && reportedRgbMode !== 0
      acceptRgbHardwareStatus = canInitializeLockedRgb
    }

    if (acceptRgbHardwareStatus && pendingRgbWrite) {
      const expected = pendingRgbWrite                                     
      const confirmed = reportedRgbEnabled === expected.enabled
        && (!expected.enabled || reportedRgbMode === expected.mode)

      if (confirmed) {
        pendingRgbWrite = null
      } else if (Date.now() < expected.expiresAt) {
        acceptRgbHardwareStatus = false
      } else {
        pendingRgbWrite = null
      }
    }

    if (acceptRgbHardwareStatus) {
      rgbMode.value = reportedRgbMode
      rgbEnabled.value = reportedRgbEnabled
      if (rgbMode.value !== 0) lastRgbMode.value = rgbMode.value
    }

    const reportedAudioModeRaw = Number(status.audioChannelMode || 0)
    const reportedAudioMode: 0 | 1 | 2 = reportedAudioModeRaw === 1 || reportedAudioModeRaw === 2
      ? reportedAudioModeRaw
      : 0
    let acceptAudioModeHardwareStatus = true

    if (pendingAudioModeWrite) {
      const expected = pendingAudioModeWrite

      if (reportedAudioMode === expected.mode) {
        // 硬件已经确认用户刚选择的模式，恢复正常跟随 0x80。
        pendingAudioModeWrite = null
      } else if (Date.now() < expected.expiresAt) {
        // 仍处于写入确认窗口：这是切换前的旧回读，不能覆盖 UI。
        acceptAudioModeHardwareStatus = false
        // deviceStatus.value 在函数开头已经指向本次 status，顺便把其中的模式字段
        // 维持为用户刚选择的值，避免仍直接读取 deviceStatus 的旧组件发生闪回。
        status.audioChannelMode = expected.mode
      } else {
        // 超时仍未确认，重新以硬件真实状态为准。
        pendingAudioModeWrite = null
      }
    }

    if (acceptAudioModeHardwareStatus) {
      audioMode.value = reportedAudioMode
      audioSplitEnabled.value = reportedAudioMode === 1
    }
    syncAudioBalanceFromStatus()

    // 0x80 里有固件版本，旧项目会合并到 deviceDetail 供固件升级页使用
    if (deviceDetail.value) {
      deviceDetail.value = {
        ...deviceDetail.value,
        softwareVersion: status.softwareVersion || deviceDetail.value.softwareVersion || '',
        bluetoothVersion: status.bluetoothVersion || deviceDetail.value.bluetoothVersion || '',
        noiseCancelVersion: status.noiseCancelVersion || deviceDetail.value.noiseCancelVersion || '',
        dongleVersion: status.dongleVersion || deviceDetail.value.dongleVersion || '',
      }
    }
  }

  async function connect(): Promise<boolean> {
    if (isConnecting.value) return false

    isConnecting.value = true
    error.value = null
    connectionStage.value = 'requesting'

    try {
      // 请求设备授权
      const success = await hidManager.connect()

      if (!success) {
        error.value = 'Failed to connect to device'
        connectionStage.value = 'error'
        return false
      }

      // 设备已打开，但业务链路需要等待状态确认
      isConnected.value = true
      connectionStage.value = 'opening'

      // 根据连接类型显示不同的等待提示
      if (connectionType.value === '2.4G') {
        connectionStage.value = 'waiting_pairing'
      } else if (connectionType.value === 'USB') {
        connectionStage.value = 'waiting_usb'
      }

      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Connection error'
      connectionStage.value = 'error'
      return false
    } finally {
      isConnecting.value = false
    }
  }

  async function disconnect(): Promise<void> {
    stopStatusPolling()
    await hidManager.disconnect()
  }

  // 轮询状态：检测设备真实连接状态，处理断线
  async function refreshStatus(): Promise<void> {
    await readStatus()
  }

  /** 返回本次真实收到的 0x80；无有效回读时返回 null。 */
  async function readStatus(): Promise<DeviceStatus | null> {
    if (statusPollingSuspended || !isConnected.value || isRefreshing.value || callBridgeWrites > 0) return null

    isRefreshing.value = true
    try {
      const status = await hidManager.getDeviceStatus()

      // OTA 暂停轮询后，之前已经发出的请求仍可能异步返回，此时必须丢弃结果。
      if (statusPollingSuspended) return null

      if (!status) {
        // 状态查询失败
        deviceStatusMissCount++
        console.warn(`设备状态查询无返回: ${deviceStatusMissCount}/${MAX_STATUS_MISSES}`)

        if (deviceStatusMissCount >= MAX_STATUS_MISSES) {
          // 连续失败，判定设备断开
          handleDeviceDisconnected('status_timeout')
        }
        return null
      }

      // 查询成功，重置失败计数
      deviceStatusMissCount = 0
      syncDeviceStatus(status)

      // 检查设备是否真正连接
      const connected = checkDeviceConnected(status)

      if (connected && !isDeviceConnected.value) {
        // 首次连接成功
        onDeviceConnectedSuccessfully(status)
      } else if (!connected && isDeviceConnected.value) {
        // 设备断开
        handleDeviceDisconnected('device_side')
      }
      return status
    } catch (error) {
      if (statusPollingSuspended) return null
      console.error('轮询错误:', error)
      deviceStatusMissCount++
      if (deviceStatusMissCount >= MAX_STATUS_MISSES) {
        handleDeviceDisconnected('status_error')
      }
      return null
    } finally {
      isRefreshing.value = false
    }
  }

  /**
   * 检查设备是否真正连接。
   * 2.4G：依据 headsetConnected === 1 判断。
   * USB：依据 linkStatus === 1 判断。
   */
  function checkDeviceConnected(status: DeviceStatus): boolean {
    if (!status) return false

    if (connectionType.value === '2.4G') {
      return status.headsetConnected === 1
    } else if (connectionType.value === 'USB') {
      return status.linkStatus === 1
    }

    return false
  }

  /**
   * 设备首次连接成功处理
   */
  function onDeviceConnectedSuccessfully(status: DeviceStatus) {
    // 检查是否在断开保护期内
    if (disconnectGuardTimer) {
      console.warn('断开保护期内忽略连接成功回包')
      return
    }

    console.log('设备连接成功')

    isDeviceConnected.value = true
    connectionStage.value = 'connected'
    deviceStatusMissCount = 0

    // 同步设备状态
    syncDeviceStatus(status)

    // 麦克风设备自动跳转到麦克风EQ页面
    if (deviceCategory.value === 'microPhone') {
      // 使用动态导入 router，避免循环依赖
      import('@/router').then(({ default: router }) => {
        const currentPath = router.currentRoute.value.path
        // 只在非特殊页面时跳转
        if (!['/login', '/register', '/upgrade', '/user'].includes(currentPath)) {
          router.push('/eq').catch(() => {
            // 忽略重复导航错误
          })
        }
      })
    }

    // 停止轮询（改为定期轮询即可，不需要频繁轮询）
    // 轮询间隔可以调整为更长（如5秒）
  }

  /**
   * 处理设备断开
   * 启动断开保护期，防止瞬时断开导致误判
   */
  function handleDeviceDisconnected(reason: string) {
    console.log('设备断开:', reason)
    resetCallBridgeState()

    // 启动断开保护期
    beginDisconnectGuard()

    isDeviceConnected.value = false
    connectionStage.value = 'waiting_pairing'
    deviceStatusMissCount = 0

    // 保持轮询，等待重新连接
  }

  /**
   * 启动断开保护期
   * 200ms 内的状态变化会被忽略，防止瞬时断开
   */
  function beginDisconnectGuard() {
    if (disconnectGuardTimer) {
      clearTimeout(disconnectGuardTimer)
    }

    disconnectGuardTimer = setTimeout(() => {
      disconnectGuardTimer = null
    }, DISCONNECT_GUARD_MS)
  }

  function normalizeConnectionStage(stage: string): string {
    const aliases: Record<string, string> = {
      authorized_found: 'requesting',
      requesting_authorization: 'requesting',
      opening_device: 'opening',
      waiting_for_pairing: 'waiting_pairing',
      waiting_for_usb_connection: 'waiting_usb',
      upgrade_mode: 'error',
    }
    return aliases[stage] || stage
  }

  // 初始化自动检测，并在启动时尝试自动连接
  async function initAutoDetection(): Promise<boolean> {
    // 只绑定一次事件监听
    hidManager.on('device-inserted', ({ connectionType }) => {
      emitDeviceConnectionTip('inserted', connectionType)
    })

    hidManager.on('device-connected', (detail: DeviceDetail) => {
      console.log('[DeviceStore] 设备连接事件:', detail.deviceName)
      deviceDetail.value = detail
      isConnected.value = true
      // 不在 0x82 详情返回时进入主界面；必须等待 0x80 状态确认业务连接
      error.value = null
    })

    hidManager.on('status-updated', (status: DeviceStatus) => {
      syncDeviceStatus(status)
      if (!statusPollingSuspended) void startStatusPolling()
    })

    hidManager.on('device-removed', ({ connectionType }) => {
      emitDeviceConnectionTip('removed', connectionType)
    })

    hidManager.on('device-disconnected', () => {
      console.log('[DeviceStore] 设备断开事件')
      resetCallBridgeState()
      noiseLevelWhileDisabled = null
      pendingAudioModeWrite = null
      isConnected.value = false
      isDeviceConnected.value = false
      deviceDetail.value = null
      deviceStatus.value = null
      connectionType.value = null
      error.value = null
      stopStatusPolling()
    })

    // data-received 不再立即触发 refreshStatus，避免与连接流程冲突；
    // 状态由定时轮询负责读取。

    // connection-stage 是底层 HID 状态机对 UI 的桥接层
    hidManager.on('connection-stage', payload => {
      console.log('[DeviceStore] 连接阶段:', payload.stage, payload.message)
      let normalizedStage = normalizeConnectionStage(payload.stage)
      if (payload.stage === 'waiting_for_pairing' && payload.connectionType === 'USB') {
        normalizedStage = 'waiting_usb'
      }
      connectionStage.value = normalizedStage
      connectionMessage.value = payload.message || null
      if (payload.stage === 'upgrade_mode') {
        error.value = payload.message || '设备处于升级模式，请进入固件升级页面'
      }
      if (payload.connectionType) {
        connectionType.value = payload.connectionType
      }
      // 打开设备后就进入主界面容器（不再显示等待遮罩）
      if (payload.stage === 'opening_device' || payload.stage === 'waiting_for_pairing') {
        isConnected.value = true
      }
      // 业务链路确认成功后，再标记为完全连接
      if (payload.stage === 'connected') {
        isConnected.value = true
        isDeviceConnected.value = true
        void startStatusPolling()
      }
      if (payload.stage === 'disconnected') {
        resetCallBridgeState()
        noiseLevelWhileDisabled = null
        isConnected.value = false
        isDeviceConnected.value = false
        stopStatusPolling()
      }
    })

    hidManager.on('error', (err: Error) => {
      if (statusPollingSuspended) return
      console.log('[DeviceStore] 错误:', err.message)
      error.value = err.message
    })

    const autoConnected = await hidManager.init()

    // 仅在 device-connected 事件后启动轮询，避免与连接初始化流程冲突。

    return autoConnected
  }

  async function startStatusPolling(): Promise<void> {
    if (statusPollingSuspended || statusPollingTimer || statusPollingStarting) return
    statusPollingStarting = true
    // 首次立即刷新，之后按旧项目 2 秒间隔持续回读 0x80。
    try {
      await refreshStatus()
      if (statusPollingSuspended || !isConnected.value || statusPollingTimer) return
      statusPollingTimer = setInterval(() => {
        void refreshStatus()
      }, STATUS_POLL_INTERVAL_MS)
    } finally {
      statusPollingStarting = false
    }
  }

  function stopStatusPolling(): void {
    if (!statusPollingTimer) return
    clearInterval(statusPollingTimer)
    statusPollingTimer = null
  }

  /** OTA 会话占用 HID 通道期间，暂停后台 0x80 状态轮询。 */
  async function suspendStatusPolling(): Promise<void> {
    statusPollingSuspended = true
    stopStatusPolling()
    // 场景/OTA 开始前，先完成已入队的 Bridge 写入；暂停后不再接受新的 Bridge 操作。
    await callBridgeWriteQueue

    // 如果之前的状态请求仍在等待响应，不要立即启动 OTA，避免两个流程同时占用通道。
    const deadline = Date.now() + 5500
    while (isRefreshing.value && Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, 50))
    }
  }

  async function resumeStatusPolling(): Promise<void> {
    statusPollingSuspended = false
    if (isConnected.value) await startStatusPolling()
  }

  // 降噪设置方法
  // ============ 命令发送操作 ============

  /**
   * 发送原始数据到设备
   */
  async function sendRawData(data: Uint8Array | string, waitForResponse = false): Promise<Uint8Array | void> {
    if (!isConnected.value) {
      throw new Error('Device not connected')
    }
    return hidManager.sendData(data, waitForResponse)
  }

  /**
   * 设置喇叭静音
   */
  async function setSpeakerMute(mute: boolean): Promise<void> {
    speakerMuted.value = mute
    const command = buildSpeakerMuteCommand(mute)
    await sendRawData(command, false)
  }

  /**
   * 设置麦克风静音
   */
  async function setMicMute(mute: boolean): Promise<void> {
    micMuted.value = mute
    const command = buildMicMuteCommand(mute)
    await sendRawData(command, false)
  }

  /**
   * 设置喇叭音量 (0-100)
   */
  async function setSpeakerVolume(volume: number): Promise<void> {
    const prev = speakerVolume.value
    const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
    speakerVolume.value = clampedVolume
    const command = buildSpeakerVolumeCommand(clampedVolume)
    await sendRawData(command, false)

    // 对齐旧版: 音量滑到0自动静音；从0增大自动取消静音
    if (clampedVolume === 0 && !speakerMuted.value) {
      await setSpeakerMute(true)
    } else if (clampedVolume > 0 && prev === 0 && speakerMuted.value) {
      await setSpeakerMute(false)
    }
  }

  /**
   * 设置麦克风音量 (0-100)
   */
  async function setMicVolume(volume: number): Promise<void> {
    const prev = micVolume.value
    const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
    micVolume.value = clampedVolume
    const command = buildMicVolumeCommand(clampedVolume)
    await sendRawData(command, false)

    // 对齐旧版: 麦克风音量滑到0自动静音；从0增大自动取消静音
    if (clampedVolume === 0 && !micMuted.value) {
      await setMicMute(true)
    } else if (clampedVolume > 0 && prev === 0 && micMuted.value) {
      await setMicMute(false)
    }
  }

  /**
   * 设置ENC降噪
   * 对齐旧版: 档位1-6，关闭时记录lastValue，开启时恢复
   */
  async function setNoiseEnabled(enabled: boolean, requestedLevel?: number): Promise<void> {
    if (enabled) {
      // 开启时恢复上次档位
      const restoreLevel = (requestedLevel ?? lastNoiseLevel.value) || 1
      noiseEnabled.value = true
      noiseLevelWhileDisabled = null
      noiseLevel.value = restoreLevel
    } else {
      // 关闭时记录当前档位
      lastNoiseLevel.value = noiseLevel.value
      noiseLevelWhileDisabled = noiseLevel.value
      noiseEnabled.value = false
    }

    const command = buildEncCommand(noiseEnabled.value, noiseLevel.value)
    await sendRawData(command, false)
  }

  async function setNoiseLevel(level: number): Promise<void> {
    const clampedLevel = Math.max(1, Math.min(6, Math.round(level)))
    noiseLevel.value = clampedLevel
    if (!noiseEnabled.value) {
      noiseLevelWhileDisabled = clampedLevel
      lastNoiseLevel.value = clampedLevel
    }

    // 如果当前降噪开启，发送命令；否则只更新值
    if (noiseEnabled.value) {
      const command = buildEncCommand(true, clampedLevel)
      await sendRawData(command, false)
    }
  }

  /**
   * 设置RGB灯效
   */
  async function setRgbEnabled(enabled: boolean): Promise<boolean> {
    // 旧协议不限制；新协议仅耳机按键静音(hardwareMuted=1)时禁止 RGB 操作。
    // 这里放在 store 层兜底，避免底部菜单、热键或后续新增入口绕过限制。
    if (rgbLockedByHardwareMute.value) return false

    if (!enabled) {
      if (rgbMode.value !== 0) {
        lastRgbMode.value = rgbMode.value
      }
      rgbEnabled.value = false
      rgbMode.value = 0
      markPendingRgbWrite(false, 0)
      const command = buildRgbCommand(false, 0)
      await sendRawData(command, false)
      return true
    }

    rgbEnabled.value = true
    if (rgbMode.value === 0) {
      rgbMode.value = lastRgbMode.value || 1
    }
    markPendingRgbWrite(true, rgbMode.value)
    const command = buildRgbCommand(true, rgbMode.value)
    await sendRawData(command, false)
    return true
  }

  async function setRgbMode(mode: number): Promise<boolean> {
    if (rgbLockedByHardwareMute.value) return false

    rgbMode.value = mode
    if (rgbEnabled.value) {
      markPendingRgbWrite(true, mode)
      const command = buildRgbCommand(true, mode)
      await sendRawData(command, false)
    }
    return true
  }

  /**
   * 对齐旧版：仅 2.4G 模式支持关机/开机（命令 0x70）
   */
  async function setPowerOn(powerOn: boolean): Promise<void> {
    if (!canUsePowerControl.value) {
      return
    }
    const command = buildPowerCommand(powerOn)
    await sendRawData(command, false)
  }

  async function togglePower(): Promise<void> {
    // 旧版: powerOffStatus=1 表示软件关机
    await setPowerOn(isPowerOff.value)
  }

  /**
   * 设置音频模式 (Mix/Split/Adaptive)
   * @param mode - 音频模式值（具体值需要根据协议定义）
   */
  async function setAudioSplit(enabled: boolean): Promise<void> {
    await setAudioMode(enabled ? 1 : 0)
  }

  async function setAudioMode(mode: 0 | 1 | 2): Promise<void> {
    const previousMode = audioMode.value
    const requestId = markPendingAudioModeWrite(mode)

    // 先更新 UI，再下发命令。后续旧的 0x80 回读会被 pending guard 挡住，
    // 直到硬件真正回报目标 mode 或等待超时。
    audioMode.value = mode
    audioSplitEnabled.value = mode === 1
    if (deviceStatus.value) deviceStatus.value.audioChannelMode = mode
    syncAudioBalanceFromStatus()

    const command = buildAudioModeCommand(mode)
    try {
      await sendRawData(command, false)
    } catch (error) {
      // 仅回滚当前这一次写入；如果期间用户又点了其他模式，不能覆盖更新的选择。
      if (pendingAudioModeWrite?.requestId === requestId) {
        pendingAudioModeWrite = null
        audioMode.value = previousMode
        audioSplitEnabled.value = previousMode === 1
        if (deviceStatus.value) deviceStatus.value.audioChannelMode = previousMode
        syncAudioBalanceFromStatus()
      }
      throw error
    }
  }

  /**
   * 设置音频平衡 (0-100)
   */
  async function setAudioBalance(value: number): Promise<void> {
    const clampedValue = Math.max(0, Math.min(100, Math.round(value)))
    audioBalanceValue.value = clampedValue
    if (deviceStatus.value) {
      const ratioKey = audioMode.value === 1 ? 'splitVolume'
        : audioMode.value === 2 ? 'adaptiveVolume' : 'mixVolume'
      deviceStatus.value[ratioKey] = clampedValue * 2
    }
    const command = buildAudioBalanceCommand(clampedValue)
    await sendRawData(command, false)
  }

  /**
   * 设置耳返开关 (新增)
   */
  async function setSidetoneEnabled(enabled: boolean): Promise<boolean> {
    const command = buildSidetoneSwitchCommand(enabled)
    await sendRawData(command, false)
    return confirmSidetoneState(enabled)
  }

  /** 仅回读确认，不重复下发命令；供手动开关和 Study 的 0x72 共用。 */
  async function confirmSidetoneState(enabled: boolean): Promise<boolean> {
    // 硬件没有“外置麦是否插入”的独立标志，不能在点击后直接假定耳返已经开启。
    // 改为等待 0x80 的 sidetoneEnabled 真正确认目标状态：
    // - 有外置麦且开启成功 -> 返回 true；
    // - 无外置麦时硬件仍保持 off -> 超时返回 false，不再误报 enabled。
    const expected = enabled ? 1 : 0
    // 原先 1.6s 的确认窗口反馈过慢。这里缩短到约 0.45s：
    // 正常硬件通常能在数次状态回读内确认；无外置麦时也能更快给出提示。
    const deadline = Date.now() + 450
    while (Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, 75))
      const status = await readStatus()
      // 跳过或失败的查询不能拿命令前缓存的状态充当硬件确认。
      if (status?.sidetoneEnabled === expected) return true
    }
    return false
  }

  /**
   * 设置耳返音量 (新增)
   * @param volume - 音量值 0-100
   */
  async function setSidetoneVolume(volume: number): Promise<void> {
    const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
    const command = buildSidetoneVolumeCommand(clampedVolume)
    await sendRawData(command, false)
  }

  /**
   * 场景应用专用 0x72：一次下发 ENC、音频模式、Bridge、Share、灯效，
   * 不再在场景流程中分别发 0x51、0x5B、0x60。
   * Study 的耳返由前端开启；其余场景的耳返及音量、静音保留最近一帧 0x80。
   */
  async function applyScenarioHardwareSettings(
    sceneValue: number,
    settings: {
      noiseLevel: number
      mixMode: number
      callBridgeOnoff: number // 场景接口三位通路值 0..7，不是硬件四位字节
      audioShareOnoff: number
      lightMode: number
    },
    options: {
      sidetoneEnabled?: boolean
      disabledNoiseLevel?: number
      disabledBridgeChannelMask?: number
      preserveCallBridge?: boolean
      preserveBtShare?: boolean
    } = {},
  ): Promise<void> {
    if (!isDeviceConnected.value || !isHeadPhone.value || !deviceStatus.value) {
      throw new Error('0x72 requires connected headset and valid 0x80 status')
    }
    const bridgeSession = callBridgeSession
    await callBridgeWriteQueue
    if (bridgeSession !== callBridgeSession || !isDeviceConnected.value || !deviceStatus.value) {
      throw new Error('Device disconnected before scenario write')
    }
    const status = deviceStatus.value
    // 场景开启/关闭也不能绕过通话限制：保留现有状态，其余场景参数正常应用。
    const bridgeByte = options.preserveCallBridge || callBridgeLockedByBluetoothCall.value
      ? callBridgeByte.value
      : sceneBridgeToHardwareByte(
        settings.callBridgeOnoff,
        options.disabledBridgeChannelMask ?? callBridgeChannelMask.value,
      )
    const targetBtShareEnabled = options.preserveBtShare || btShareLockedByBluetoothCall.value
      ? status.btShareEnabled === 1
      : settings.audioShareOnoff === 1
    const bridgeMask = bridgeByte & BRIDGE_ALL_CHANNELS
    const bridgeEnabled = (bridgeByte & 0x01) !== 0
    // 静音位保留；低七位音量仅取协议定义的 0~100，防止损坏的状态字节被透传。
    const packedStatusVolume = (raw: number | undefined, muted: number | undefined, volume: number | undefined): number => {
      const isMuted = raw != null ? (raw & 0x80) !== 0 : muted === 1
      const level = raw != null ? (raw & 0x7F) : volume ?? 0
      return (isMuted ? 0x80 : 0) | Math.max(0, Math.min(100, Math.round(level)))
    }
    // 场景初次应用时仅 Gaming 允许默认开灯；255 关闭恢复仍使用原始快照。
    // 仅在应用时设置默认值，用户随后仍可手动开启灯光。
    const sceneLedMode = sceneValue > 1 ? 0 : settings.lightMode
    // 新协议按键静音时 RGB 被锁定，不能用场景命令绕过。
    const targetLedMode = rgbLockedByHardwareMute.value
      ? (rgbEnabled.value ? rgbMode.value : 0)
      : sceneLedMode
    if (rgbLockedByHardwareMute.value && targetLedMode !== sceneLedMode) {
      console.info('[SCENE][0x72] RGB remains unchanged while hardware microphone is muted')
    }
    const noiseOn = settings.noiseLevel > 0
    // sceneValue=0 时要恢复场景开启前的备用 ENC 档位；服务端 255 没有独立
    // “关降噪后的档位”字段，缺失时使用默认 Level 1，不能沿用 Gaming 的 Level 6。
    const disabledNoiseLevel = sceneValue === 0
      ? Math.max(1, Math.min(6, Math.round(options.disabledNoiseLevel ?? 1)))
      : Math.max(1, Math.min(6, noiseLevel.value))
    const cmd = buildScenarioApplyCommand({
      sceneValue,
      mixVolume: status.mixVolume ?? 100,
      splitVolume: status.splitVolume ?? 100,
      // 三种比例来自 0x80 的 B[2]/B[3]/B[37]；0 是有效值，不能被默认 100 覆盖。
      adaptiveVolume: status.adaptiveVolume ?? 100,
      audioMode: settings.mixMode,
      callBridgeEnabled: bridgeEnabled,
      callBridgeChannelMask: bridgeMask,
      // 0x72 B0=4 为 Study；后端无耳返字段，直接在 B6 bit7 开启。
      sidetoneEnabled: options.sidetoneEnabled ?? (sceneValue === 4 || status.sidetoneEnabled === 1),
      sidetoneVolume: status.sidetoneVolume ?? 0,
      btShareEnabled: targetBtShareEnabled,
      noiseEnabled: noiseOn,
      noiseLevel: noiseOn ? settings.noiseLevel : disabledNoiseLevel,
      ledMode: targetLedMode,
      headsetMic: packedStatusVolume(status.usbMicByte, status.usbMicMute, status.usbMicVolumeLevel),
      headsetSpeaker: packedStatusVolume(status.usbSpeakerByte, status.usbSpeakerMute, status.usbSpeakerVolumeLevel),
      dongleMic: packedStatusVolume(status.dongleMicByte, status.dongleMicMute, status.dongleMicVolumeLevel),
      dongleSpeaker: packedStatusVolume(status.dongleSpeakerByte, status.dongleSpeakerMute, status.dongleSpeakerVolumeLevel),
    })
    console.debug('[SCENE][0x72] sceneValue=', sceneValue, 'hex=', Array.from(cmd).map(v => v.toString(16).padStart(2, '0').toUpperCase()).join(' '))
    await sendRawData(cmd, false)

    // WebHID 写入成功后先同步 UI。模式及灯效沿用已有 0x80 防闪回窗口，
    // 窗口结束后仍以耳机真实状态为准。
    if (audioMode.value !== settings.mixMode) markPendingAudioModeWrite(settings.mixMode as 0 | 1 | 2)
    audioMode.value = settings.mixMode as 0 | 1 | 2
    audioSplitEnabled.value = settings.mixMode === 1
    status.audioChannelMode = settings.mixMode
    syncAudioBalanceFromStatus()

    noiseEnabled.value = noiseOn
    if (noiseOn) {
      noiseLevelWhileDisabled = null
      noiseLevel.value = settings.noiseLevel
      lastNoiseLevel.value = settings.noiseLevel
    } else if (sceneValue === 0) {
      // 未登录刷新/关闭场景必须恢复可见档位，不能只设置 ENC OFF。
      noiseLevelWhileDisabled = disabledNoiseLevel
      noiseLevel.value = disabledNoiseLevel
      lastNoiseLevel.value = disabledNoiseLevel
    }
    status.noiseReductionEnabled = noiseOn ? 1 : 0
    status.noiseReductionMode = noiseOn ? settings.noiseLevel : noiseLevel.value
    confirmedCallBridgeByte = bridgeByte
    if (bridgeMask) confirmedBridgeRestoreMask = bridgeMask
    pendingCallBridgeWrite = { byte: bridgeByte, expiresAt: Date.now() + BRIDGE_WRITE_GUARD_MS }
    applyCallBridgeByte(bridgeByte)
    status.btShareEnabled = targetBtShareEnabled ? 1 : 0

    if (!rgbLockedByHardwareMute.value) {
      const targetEnabled = targetLedMode !== 0
      if (rgbMode.value !== targetLedMode || rgbEnabled.value !== targetEnabled) {
        markPendingRgbWrite(targetEnabled, targetLedMode)
      }
      rgbMode.value = targetLedMode
      rgbEnabled.value = targetEnabled
      status.ledMode = targetLedMode
      if (targetLedMode !== 0) lastRgbMode.value = targetLedMode
    }
  }

  /** 切换蓝牙音频共享（旧协议：0x5B / 0x04）。 */
  async function setBtShare(enabled: boolean): Promise<void> {
    if (btShareLockedByBluetoothCall.value) throw new Error('AUDIO_SHARE_LOCKED_DURING_BLUETOOTH_CALL')
    await sendRawData(buildBtShareSwitchCommand(enabled), false)
    if (deviceStatus.value) deviceStatus.value.btShareEnabled = enabled ? 1 : 0
  }

  /** 0x5B/0x05：bit0 总开关，bit1..3 通路；每次操作只发送一条完整状态命令。 */
  async function setCallBridge(enabled: boolean, channelMask?: number): Promise<void> {
    if (!isConnected.value || !isDeviceConnected.value) throw new Error('Device not connected')
    if (callBridgeLockedByBluetoothCall.value) throw new Error('BRIDGE_LOCKED_DURING_BLUETOOTH_CALL')
    if (statusPollingSuspended) throw new Error('Device is busy applying settings')
    const mask = channelMask ?? (enabled ? callBridgeRestoreChannelMask.value : callBridgeChannelMask.value)
    const byte = packCallBridgeByte(enabled && mask !== 0, mask)
    const command = buildCallBridgeSwitchCommand((byte & 1) !== 0, mask)
    const session = callBridgeSession
    const requestId = ++callBridgeRequestId
    callBridgeWrites += 1
    pendingCallBridgeWrite = { byte, expiresAt: Number.POSITIVE_INFINITY }
    applyCallBridgeByte(byte)

    const task = callBridgeWriteQueue.then(async () => {
      // 先等当前 0x80 读完，避免 HID 正在等待响应时拒绝 Bridge 写入。
      const deadline = Date.now() + 5500
      while (isRefreshing.value && Date.now() < deadline && session === callBridgeSession) {
        await new Promise(resolve => setTimeout(resolve, 25))
      }
      if (session !== callBridgeSession || !isConnected.value || !isDeviceConnected.value) {
        throw new Error('Device disconnected before Bridge write')
      }
      if (isRefreshing.value) throw new Error('Device status request is still busy')
      // 入队后可能才接入蓝牙通话，下发前再次核对，失败沿用已有的状态回滚。
      if (callBridgeLockedByBluetoothCall.value) throw new Error('BRIDGE_LOCKED_DURING_BLUETOOTH_CALL')
      await sendRawData(command, false)
      if (session !== callBridgeSession) throw new Error('Device disconnected during Bridge write')
      confirmedCallBridgeByte = byte
      if (mask) confirmedBridgeRestoreMask = mask
    })
    // 后续点击不因上一条失败而中断；完整位图可独立设置最终状态。
    callBridgeWriteQueue = task.catch(() => {})
    try {
      await task
    } catch (error) {
      if (session === callBridgeSession && requestId === callBridgeRequestId) {
        pendingCallBridgeWrite = null
        lastCallBridgeChannelMask = confirmedBridgeRestoreMask
        applyCallBridgeByte(confirmedCallBridgeByte)
      }
      throw error
    } finally {
      if (session === callBridgeSession) {
        callBridgeWrites -= 1
        if (callBridgeWrites === 0 && pendingCallBridgeWrite) {
          pendingCallBridgeWrite.expiresAt = Date.now() + BRIDGE_WRITE_GUARD_MS
        }
      }
    }
  }

  async function setVoiceEffect(effect: VoiceType): Promise<void> {
    const effectMap: Record<VoiceType, number> = {
      normal: 0,
      boy: 1,
      girl: 2,
      child: 3,
      robot: 4,
      monster: 5,
    }
    voiceEffect.value = effect
    await sendRawData(buildVoiceEffectCommand(effectMap[effect]), false)
  }

  /**
   * 发送 EQ 登录状态 (新增)
   * @param isLoggedIn - 是否已登录
   */
  async function sendEqLoginStatus(isLoggedIn: boolean): Promise<void> {
    const command = buildEqLoginStatusCommand(isLoggedIn)
    await sendRawData(command, false)
  }

  // ============ 旧版兼容接口 ============

  /**
   * 兼容旧版: 切换喇叭静音状态
   */
  async function toggleSpeakerMute(): Promise<void> {
    await setSpeakerMute(!speakerMuted.value)
  }

  /**
   * 兼容旧版: 切换麦克风静音状态
   */
  async function toggleMicMute(): Promise<void> {
    await setMicMute(!micMuted.value)
  }

  /** 兼容 AI 助手：喇叭静音 */
  async function setMute(mute: boolean): Promise<void> {
    await setSpeakerMute(mute)
  }

  return {
    // 状态
    isConnected,
    isDeviceConnected,
    isConnecting,
    deviceDetail,
    deviceStatus,
    connectionType,
    connectionStage,
    connectionMessage,
    error,
    noiseEnabled,
    noiseLevel,
    lastNoiseLevel,
    speakerMuted,
    micMuted,
    speakerVolume,
    micVolume,
    rgbEnabled,
    rgbMode,
    rgbBrightness,
    lastRgbMode,
    audioSplitEnabled,
    audioMode,
    audioBalanceValue,
    voiceEffect,
    callBridgeByte,
    callBridgeEnabled,
    callBridgeChannelMask,
    callBridgeRestoreChannelMask,
    isBluetoothInCall,
    supportsCallBridgeDuringBluetoothCall,
    callBridgeLockedByBluetoothCall,
    btShareLockedByBluetoothCall,
    // 计算属性
    deviceCategory,
    isHeadPhone,
    isMicroPhone,
    batteryLevel,
    isCharging,
    isPowerOff,
    // 设备能力（支持扩展新设备类型）
    deviceCapabilities,
    canUseEq,
    canUseNoiseReduction,
    canUseSpecialAudio,
    canUseLed,
    canUseHotkey,
    canUseLanguageSwitch,
    canUseFirmwareUpgrade,
    canUsePowerControl,
    canUseVoiceChange,
    usesNewProtocol,
    rgbLockedByHardwareMute,
    usesBluetoothPlayingForAudioSplit,
    audioSplitBluetoothActive,
    // 操作
    connect,
    disconnect,
    refreshStatus,
    initAutoDetection,
    startStatusPolling,
    stopStatusPolling,
    suspendStatusPolling,
    resumeStatusPolling,
    setNoiseEnabled,
    setNoiseLevel,
    setSpeakerMute,
    setMicMute,
    setSpeakerVolume,
    setMicVolume,
    setRgbEnabled,
    setRgbMode,
    togglePower,
    setPowerOn,
    setAudioSplit,
    setAudioMode,
    setAudioBalance,
    applyScenarioHardwareSettings,
    setSidetoneEnabled,
    confirmSidetoneState,
    setSidetoneVolume,
    setBtShare,
    setCallBridge,
    setVoiceEffect,
    sendEqLoginStatus,
    toggleSpeakerMute,
    toggleMicMute,
    setMute,
    sendRawData,
  }
})
